// number-extensions.ts

declare global {
    interface Number {
        /**
         * Round to 2 decimal places using standard rounding (HALF_UP).
         */
        fix2(): number;

        /**
         * Round to 9 decimal places using standard rounding (HALF_UP).
         */
        fix9(): number;

        /**
         * Round to 9 decimal places using banker's rounding (HALF_EVEN).
         */
        roundHalfEven9(): number;

        /**
         * Round to the requested number of decimal places using banker's rounding (HALF_EVEN).
         */
        roundHalfEvenToPrecision(precision: number): number;
    }
}

const SCALE = 9 as const;
const SCALE_2 = 2 as const;

export function roundToPrecision(value: number, precision: number): number {
    if (!Number.isFinite(value)) return value;
    const factor = 10 ** precision;
    return (Math.sign(value) * Math.round((Math.abs(value) + Number.EPSILON) * factor) / factor);
}

/**
 * Banker’s rounding (HALF_EVEN) to the requested number of decimal places.
 */
export function roundHalfEvenToPrecision(value: number, precision: number): number {
    if (!Number.isFinite(value)) return value;
    const factor = 10 ** precision;
    const scaledValue = value * factor;
    const floor = Math.floor(scaledValue);
    const difference = scaledValue - floor;
    const epsilon = Number.EPSILON * Math.abs(scaledValue);

    if (difference > 0.5 + epsilon) return (floor + 1) / factor;
    if (difference < 0.5 - epsilon) return floor / factor;

    const even = (floor % 2 === 0) ? floor : (floor + 1);
    return even / factor;
}

/**
 * Standard rounding to 2 decimals (HALF_UP).
 */
export function fix2(n: number): number {
    if (!Number.isFinite(n)) return n;
    const f = 10 ** SCALE_2;
    return Math.round((n + Number.EPSILON) * f) / f;
}

/**
 * Standard rounding to 9 decimals (HALF_UP).
 */
export function fix9(n: number): number {
    if (!Number.isFinite(n)) return n;
    const f = 10 ** SCALE;
    // Adding EPSILON helps with values like 1.005
    return Math.round((n + Number.EPSILON) * f) / f;
}

/**
 * Banker’s rounding (HALF_EVEN) to 9 decimals.
 */
export function roundHalfEven9(n: number): number {
    if (!Number.isFinite(n)) return n;
    const f = 10 ** SCALE;
    const x = n * f;
    const floor = Math.floor(x);
    const diff = x - floor;
    // relative epsilon with magnitude
    const epsilon = Number.EPSILON * Math.abs(x);

    if (diff > 0.5 + epsilon) return (floor + 1) / f;
    if (diff < 0.5 - epsilon) return floor / f;

    // tie: diff ~ 0.5 -> round to even integer
    const even = (floor % 2 === 0) ? floor : (floor + 1);
    return even / f;
}

/**
 * Define a non-enumerable method on Number.prototype if absent.
 */
function defineIfAbsent<TArgs extends number[]>(name: string, fn: (n: number, ...args: TArgs) => number): void {
    const proto = Number.prototype as any;
    if (!Object.hasOwn(proto, name)) {
        Object.defineProperty(proto, name, {
            value: function (this: number, ...args: TArgs): number { return fn(this.valueOf(), ...args); },
            writable: false,
            enumerable: false,
            configurable: true
        });
    }
}

try {
    defineIfAbsent('fix2', fix2);
    defineIfAbsent('fix9', fix9);
    defineIfAbsent('roundHalfEven9', roundHalfEven9);
    defineIfAbsent('roundHalfEvenToPrecision', roundHalfEvenToPrecision);
} catch (error: unknown) {
    const message = error instanceof Error ? error.message 
    : typeof error === 'string' ? error 
    : typeof error === 'object' && error !== null ? JSON.stringify(error) 
    : String(error);
    console.warn('Failed to extend Number prototype:', message);
}